// End-of-shift cash count: waiter enters counted cash; expected = their cash tickets of that shift; difference is logged.
import React, { useCallback, useEffect, useState } from 'react';
import { Coins, Trash2 } from 'lucide-react';
import { apiCall } from '../_shared';
import { money } from './BarTab';
import { shiftOf } from './StaffShifts';

const todayISO = () => new Date().toISOString().slice(0, 10);

export const CashCount = ({ day: dayProp, canManage = false, staffOptions = [] }) => {
  const [day, setDay] = useState(dayProp || todayISO());
  const [shift, setShift] = useState(shiftOf(new Date().toISOString()));
  const [staff, setStaff] = useState('');
  const [counted, setCounted] = useState('');
  const [note, setNote] = useState('');
  const [expected, setExpected] = useState(null);
  const [data, setData] = useState({ counts: [], shifts: [] });
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (dayProp) setDay(dayProp); }, [dayProp]);
  const load = useCallback(() => apiCall(`/api/admin/bar/cash-counts?day=${day}`).then(setData).catch(() => {}), [day]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const q = `day=${day}&shift=${shift}${staff ? `&staff=${encodeURIComponent(staff)}` : ''}`;
    apiCall(`/api/admin/bar/cash-counts/expected?${q}`).then(setExpected).catch(() => setExpected(null));
  }, [day, shift, staff]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setMsg('');
    try {
      const r = await apiCall('/api/admin/bar/cash-counts', { method: 'POST', body: JSON.stringify({ day, shift, counted_cash: Number(counted), note: note || null, staff: staff || null }) });
      setMsg(r.status === 'ok' ? `Counted ${money(r.counted)} — matches expected ${money(r.expected)}. Logged.` : `Counted ${money(r.counted)} vs expected ${money(r.expected)}: ${r.status.toUpperCase()} ${r.difference > 0 ? '+' : ''}${money(r.difference)}. Logged.`);
      setCounted(''); setNote(''); load();
    } catch (err) { setMsg(err.message); } finally { setBusy(false); }
  };
  const remove = async (id) => { if (!window.confirm('Delete this cash count?')) return; try { await apiCall(`/api/admin/bar/cash-counts/${id}`, { method: 'DELETE' }); load(); } catch (err) { setMsg(err.message); } };

  const diff = counted !== '' && expected ? Number(counted) - expected.expected : null;
  const pill = (s) => s === 'ok' ? 'bg-emerald-100 text-emerald-800' : s === 'short' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800';
  const shiftOpts = (data.shifts.length ? data.shifts : [{ id: 'morning', label: 'Morning 07–15' }, { id: 'afternoon', label: 'Afternoon 15–23' }, { id: 'night', label: 'Night 23–07' }]).map((s) => <option key={s.id} value={s.id}>{s.label}</option>);
  const staffOpts = staffOptions.map((s) => <option key={s} value={s}>{s}</option>);
  const countList = data.counts;
  const rows = countList.map((c) => (
    <li key={c.id} className="flex flex-wrap items-center gap-2 border-t py-1.5 text-xs" data-testid={`cash-count-${c.id}`}>
      <span className="text-slate-500 w-28">{c.shift}</span><span className="font-semibold w-24">{c.staff}</span>
      <span>expected {money(c.expected)} · counted <b>{money(c.counted)}</b></span>
      <span className={`rounded-full px-2 py-0.5 font-semibold ${pill(c.status)}`} data-testid={`cash-status-${c.id}`}>{c.status} {c.difference > 0 ? '+' : ''}{money(c.difference)}</span>
      {c.note && <span className="text-slate-500 italic">“{c.note}”</span>}
      <span className="text-slate-400 ml-auto">{String(c.at).slice(11, 16)} by {c.by}</span>
      {canManage && <button type="button" onClick={() => remove(c.id)} className="text-slate-400 hover:text-red-600" data-testid={`cash-delete-${c.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
    </li>
  ));

  return (
    <div className="bg-white border rounded-xl p-4" data-testid="cash-count">
      <h3 className="font-semibold text-slate-900 mb-2 flex items-center gap-2 text-sm"><Coins className="w-4 h-4" /> End-of-shift cash count</h3>
      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs items-end">
        {!dayProp && <label>Day<input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="block w-full border rounded px-2 py-1" data-testid="cash-day" /></label>}
        <label>Shift<select value={shift} onChange={(e) => setShift(e.target.value)} className="block w-full border rounded px-2 py-1" data-testid="cash-shift">{shiftOpts}</select></label>
        {canManage && staffOptions.length > 0 && <label>Staff<select value={staff} onChange={(e) => setStaff(e.target.value)} className="block w-full border rounded px-2 py-1" data-testid="cash-staff"><option value="">me</option>{staffOpts}</select></label>}
        <label>Counted cash €<input type="number" step="0.01" min="0" required value={counted} onChange={(e) => setCounted(e.target.value)} className="block w-full border rounded px-2 py-1" data-testid="cash-counted" /></label>
        <label className="col-span-2 md:col-span-1">Note<input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. €5 tip jar" className="block w-full border rounded px-2 py-1" data-testid="cash-note" /></label>
        <button type="submit" disabled={busy || counted === ''} className="bg-slate-900 text-white rounded px-3 py-1.5 font-semibold disabled:opacity-50" data-testid="cash-submit">Log count</button>
      </form>
      {expected && (
        <p className="text-xs text-slate-600 mt-2" data-testid="cash-expected">
          Expected cash for <b>{expected.staff}</b> · {shift}: <b>{money(expected.expected)}</b> from {expected.tickets} cash ticket{expected.tickets === 1 ? '' : 's'}
          {diff !== null && <span className={`ml-2 rounded-full px-2 py-0.5 font-semibold ${pill(Math.abs(diff) <= 0.5 ? 'ok' : diff < 0 ? 'short' : 'over')}`} data-testid="cash-diff">{Math.abs(diff) <= 0.5 ? 'matches' : diff < 0 ? 'short' : 'over'} {diff > 0 ? '+' : ''}{money(diff)}</span>}
        </p>
      )}
      {msg && <p className="text-xs text-slate-700 mt-2" data-testid="cash-msg">{msg}</p>}
      {rows.length > 0 && <ul className="mt-3">{rows}</ul>}
    </div>
  );
};

export default CashCount;
